<template>
  <div class="oss-panel">
    <p class="oss-hint">
      上传后得到播放链接，填进学习资源的 URL。勾选加密时按 HLS AES-128 切片（与仓库里的加密视频相同），密钥只发给有学习权限的人。
    </p>

    <el-form label-position="top" class="oss-form" @submit.prevent>
      <el-form-item label="标题">
        <el-input v-model="form.title" maxlength="80" placeholder="不填则使用文件名" />
      </el-form-item>
      <el-form-item label="关联课程">
        <el-select v-model="form.kcId" clearable filterable placeholder="可不选，稍后在资源里使用链接" style="width: 100%">
          <el-option v-for="c in courses" :key="c.kcId" :label="c.name" :value="c.kcId" />
        </el-select>
      </el-form-item>
      <el-form-item label="文件" required>
        <input ref="fileInput" type="file" accept=".mp4,.mov,.mkv,.webm,.m4v,.mp3,.m4a,.wav,.aac,.ogg,.flac,video/*,audio/*" @change="onFile" />
        <p v-if="fileName" class="file-name">{{ fileName }}</p>
      </el-form-item>
      <el-form-item>
        <el-checkbox v-model="form.encrypt">加密（HLS AES-128）</el-checkbox>
        <p class="field-hint">加密会转码并切片，大文件可能需要几分钟。未勾选则原文件直接上传。</p>
      </el-form-item>
      <el-button type="primary" :loading="uploading" @click="submitUpload">上传到阿里云</el-button>
    </el-form>

    <div v-if="lastUrl" class="last-url">
      <span>播放链接</span>
      <el-input :model-value="lastUrl" readonly />
      <el-button @click="copyText(lastUrl)">复制</el-button>
      <el-button type="primary" plain @click="goFillResource(lastUrl, form.kcId)">填入学习资源</el-button>
    </div>

    <div class="list-bar">
      <el-input v-model="keyword" placeholder="按标题查找" clearable @keyup.enter="reload" />
      <el-button @click="reload">查询</el-button>
    </div>

    <el-table :data="rows" v-loading="loading" empty-text="还没有云对象">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="title" label="标题" min-width="140" />
      <el-table-column label="类型" width="90">
        <template #default="s">{{ s.row.mediaType === 'audio' ? '音频' : '视频' }}</template>
      </el-table-column>
      <el-table-column label="加密" width="70">
        <template #default="s">{{ s.row.encrypted ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column label="播放链接" min-width="220">
        <template #default="s">
          <a v-if="s.row.publicUrl" :href="s.row.publicUrl" target="_blank" rel="noopener">{{ s.row.publicUrl }}</a>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="大小" width="100">
        <template #default="s">{{ formatSize(s.row.sizeBytes) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="s">{{ s.row.status === 'ready' ? '就绪' : s.row.status }}</template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right">
        <template #default="s">
          <el-button link type="primary" :disabled="!s.row.publicUrl" @click="copyText(s.row.publicUrl)">复制链接</el-button>
          <el-button link type="primary" :disabled="!s.row.publicUrl" @click="goFillResource(s.row.publicUrl, s.row.kcId)">填入资源</el-button>
          <el-button link type="primary" @click="openEdit(s.row)">编辑</el-button>
          <el-button link type="danger" @click="removeRow(s.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        @current-change="changePage"
      />
    </div>

    <el-dialog v-model="editVisible" title="编辑云对象" width="480px">
      <el-form label-position="top">
        <el-form-item label="标题">
          <el-input v-model="editForm.title" maxlength="80" />
        </el-form-item>
        <el-form-item label="关联课程">
          <el-select v-model="editForm.kcId" clearable filterable placeholder="不关联" style="width: 100%">
            <el-option v-for="c in courses" :key="c.kcId" :label="c.name" :value="c.kcId" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveEdit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { deleteCourseOss, listCourseOss, updateCourseOss, uploadCourseOss } from '@/utils/courseOss'

export default {
  name: 'CourseOssPanel',
  props: {
    adminMode: { type: Boolean, default: false },
    initialKcId: { type: [Number, String], default: null }
  },
  data() {
    return {
      courses: [],
      form: { title: '', kcId: null, encrypt: false },
      file: null,
      fileName: '',
      uploading: false,
      lastUrl: '',
      keyword: '',
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      editVisible: false,
      saving: false,
      editForm: { id: 0, title: '', kcId: null }
    }
  },
  created() {
    if (this.initialKcId) this.form.kcId = Number(this.initialKcId) || null
    this.loadCourses()
    this.reload()
  },
  methods: {
    authJson() {
      return {
        Authorization: `Bearer ${localStorage.getItem('jwt_token') || ''}`,
        'Content-Type': 'application/json'
      }
    },
    async loadCourses() {
      const url = this.adminMode
        ? '/api/v1/knowledge/courses/list'
        : '/api/v1/creator/knowledge/courses/list'
      const body = this.adminMode
        ? { page: 1, pageSize: 200, keyword: '', adminView: 1 }
        : { page: 1, pageSize: 200, keyword: '' }
      try {
        const res = await fetch(url, { method: 'POST', headers: this.authJson(), body: JSON.stringify(body) })
        const data = await res.json()
        if (data.code === 200) this.courses = data.data?.list || []
      } catch (_) { /* 课程下拉失败不影响上传 */ }
    },
    onFile(e) {
      const f = e.target.files && e.target.files[0]
      this.file = f || null
      this.fileName = f ? f.name : ''
      if (f && !this.form.title) {
        this.form.title = f.name.replace(/\.[^.]+$/, '')
      }
    },
    async submitUpload() {
      if (!this.file) {
        ElMessage.warning('请选择文件')
        return
      }
      this.uploading = true
      try {
        const data = await uploadCourseOss({
          file: this.file,
          title: this.form.title,
          encrypt: this.form.encrypt,
          kcId: this.form.kcId || 0
        })
        this.lastUrl = data.data?.publicUrl || ''
        ElMessage.success(this.form.encrypt ? '已加密并上传' : '已上传')
        this.file = null
        this.fileName = ''
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        this.reload()
      } catch (e) {
        ElMessage.error(e.message || '上传失败')
      } finally {
        this.uploading = false
      }
    },
    async reload() {
      this.loading = true
      try {
        const data = await listCourseOss({
          page: this.page,
          pageSize: this.pageSize,
          keyword: this.keyword
        })
        this.rows = data.data?.list || []
        this.total = data.data?.total || 0
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    changePage(p) {
      this.page = p
      this.reload()
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success('已复制播放链接')
      } catch (_) {
        ElMessage.warning(text)
      }
    },
    goFillResource(url, kcId) {
      const path = this.adminMode ? '/admin/knowledge/resources' : '/creator/knowledge/resources'
      this.$router.push({ path, query: { kcId: kcId || undefined, url } })
    },
    openEdit(row) {
      this.editForm = { id: row.id, title: row.title, kcId: row.kcId || null }
      this.editVisible = true
    },
    async saveEdit() {
      this.saving = true
      try {
        await updateCourseOss({
          id: this.editForm.id,
          title: this.editForm.title,
          kcId: this.editForm.kcId || 0
        })
        ElMessage.success('已保存')
        this.editVisible = false
        this.reload()
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async removeRow(row) {
      try {
        await ElMessageBox.confirm(
          `删除「${row.title || row.id}」会同时删掉阿里云上的文件。若学习资源还在用这个链接，会拒绝删除。`,
          '删除云对象',
          { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
        )
      } catch (_) {
        return
      }
      try {
        await deleteCourseOss(row.id)
        ElMessage.success('已删除')
        this.reload()
      } catch (e) {
        ElMessage.error(e.message || '删除失败')
      }
    },
    formatSize(n) {
      const v = Number(n) || 0
      if (v < 1024) return v + ' B'
      if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB'
      return (v / 1024 / 1024).toFixed(1) + ' MB'
    }
  }
}
</script>

<style scoped>
.oss-hint { color: #666; margin-bottom: 16px; line-height: 1.6; }
.oss-form { max-width: 640px; }
.field-hint, .file-name { color: #888; font-size: 13px; margin: 6px 0 0; }
.last-url { display: flex; gap: 8px; align-items: center; margin: 16px 0; flex-wrap: wrap; }
.last-url .el-input { flex: 1; min-width: 240px; }
.list-bar { display: flex; gap: 8px; margin: 18px 0 12px; max-width: 480px; }
.pager { margin-top: 12px; display: flex; justify-content: flex-end; }
a { word-break: break-all; }
</style>
